import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Mail, Lock, User, Phone, Check, Loader2, ArrowRight, Tag, CheckCircle2, XCircle, PartyPopper, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { billingApi, publicPlansApi, affiliateApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";
import { softwareApplicationSchema } from "../components/seo/schema";
import { trackEvent } from "../lib/analytics";
import GoogleButton, { OrDivider } from "../components/GoogleButton";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function PlanAndCouponSection({ couponCode, setCouponCode, couponState, couponInfo, promoCode, setPromoCode, promoState, promoInfo, plans, term, setTerm, planId, setPlanId, seatsValue, onSeatsChange, minSeats, total }) {
  return (
    <>
      <div>
        <label className="block text-[14px] font-semibold text-slate-700 mb-1.5">
          DISCOUNT CODE <span className="text-slate-400 font-normal">(Optional)</span>
        </label>
        <div className="relative">
          <Tag className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder="Have a code? Enter it here"
            data-testid="signup-coupon-input"
            className={`w-full h-10 pl-9 pr-9 rounded-lg border text-[15.5px] uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 ${
              couponState === "valid" ? "border-emerald-400" : couponState === "invalid" ? "border-rose-400" : "border-slate-400"
            }`}
          />
          {couponState === "checking" && <Loader2 className="h-4 w-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
          {couponState === "valid" && <CheckCircle2 className="h-4 w-4 text-emerald-500 absolute right-3 top-1/2 -translate-y-1/2" />}
          {couponState === "invalid" && <XCircle className="h-4 w-4 text-rose-500 absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        {couponState === "invalid" && (
          <div data-testid="signup-coupon-invalid-message" className="mt-1.5 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-[14px] text-slate-500">
            We couldn't find that code — double-check it, or continue without one for the standard 14-day trial.
          </div>
        )}
      </div>

      {couponState === "valid" && couponInfo && (
        <div
          data-testid="signup-coupon-valid-banner"
          className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-300 shadow-sm"
        >
          <PartyPopper className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-[15.5px] font-bold text-emerald-800">
              Your code is valid and you now have a {couponInfo.trialDays} Day free subscription. Welcome to Service Guru Pro!
            </div>
            {couponInfo.creditsDiscountPct > 0 && (
              <div className="text-[14px] text-emerald-700 mt-0.5">Plus {couponInfo.creditsDiscountPct}% off AI credits during your trial.</div>
            )}
          </div>
        </div>
      )}

      <div>
        <label className="block text-[14px] font-semibold text-slate-700 mb-1.5">
          PROMO CODE <span className="text-slate-400 font-normal">(Optional)</span>
        </label>
        <div className="relative">
          <Tag className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            placeholder="Have a promo code? Enter it here"
            data-testid="signup-promo-input"
            className={`w-full h-10 pl-9 pr-9 rounded-lg border text-[15.5px] uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 ${
              promoState === "valid" ? "border-emerald-400" : promoState === "invalid" ? "border-rose-400" : "border-slate-400"
            }`}
          />
          {promoState === "checking" && <Loader2 className="h-4 w-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
          {promoState === "valid" && <CheckCircle2 className="h-4 w-4 text-emerald-500 absolute right-3 top-1/2 -translate-y-1/2" />}
          {promoState === "invalid" && <XCircle className="h-4 w-4 text-rose-500 absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        {promoState === "invalid" && (
          <div data-testid="signup-promo-invalid-message" className="mt-1.5 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-[14px] text-slate-500">
            We couldn't find that promo code — double-check it, or continue without one.
          </div>
        )}
        {promoState === "valid" && promoInfo && (
          <div data-testid="signup-promo-valid-message" className="mt-1.5 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[14px] text-amber-700 font-medium">
            {promoInfo.discountPercent}% off AI Credit purchases applied to your account.
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-[14px] font-semibold text-slate-700">Choose a plan</label>
          <div className="inline-flex rounded-lg bg-slate-100 p-0.5 text-[13.5px] font-semibold">
            {["monthly", "annual"].map((t) => (
              <button
                key={t} type="button" data-testid={`signup-term-${t}`}
                onClick={() => setTerm(t)}
                className={`px-2.5 py-1 rounded-md capitalize transition-colors ${term === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              >
                {t}{t === "annual" && <span className="text-emerald-600 ml-1">save</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {plans.map((p) => {
            const price = term === "annual" ? p.perSeatAnnual : p.perSeatMonthly;
            const active = planId === p.id;
            return (
              <button
                key={p.id} type="button" data-testid={`signup-plan-${p.id}`}
                onClick={() => setPlanId(p.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors ${active ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500/30" : "border-slate-400 hover:border-slate-500"}`}
              >
                <div>
                  <div className="text-[15.5px] font-semibold text-slate-900">{p.name}</div>
                  <div className="text-[13.5px] text-slate-500">{(p.features || [])[0] || "Per-seat billing"}{p.minSeats > 1 ? ` · min ${p.minSeats} seats` : ""}</div>
                </div>
                <div className="text-right">
                  <div className="text-[16px] font-bold text-slate-900">{money(price)}</div>
                  <div className="text-[12.5px] text-slate-400">/seat/{term === "annual" ? "yr" : "mo"}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <label className="text-[14px] font-semibold text-slate-700">Number of seats</label>
        <input
          type="number" min={minSeats} value={seatsValue}
          data-testid="signup-seats"
          onChange={onSeatsChange}
          className="w-24 h-9 px-3 rounded-lg border border-slate-400 text-[15.5px] text-right focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        />
      </div>

      <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-300">
        <span className="text-[14.5px] text-slate-500">After trial — {term === "annual" ? "annual" : "monthly"} total</span>
        <span className="text-[18px] font-bold text-slate-900" data-testid="signup-total">{money(total)}<span className="text-[13px] font-medium text-slate-400">/{term === "annual" ? "yr" : "mo"}</span></span>
      </div>
    </>
  );
}

export default function SignupCompany() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [term, setTerm] = useState(params.get("term") === "annual" ? "annual" : "monthly");
  const [planId, setPlanId] = useState("");
  const [form, setForm] = useState({
    companyName: "", ownerName: "", ownerEmail: "", ownerPassword: "", contactPhone: "", seats: 1,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState("idle"); // idle | checking | valid | invalid
  const [couponInfo, setCouponInfo] = useState(null);
  const couponDebounce = useRef(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoState, setPromoState] = useState("idle"); // idle | checking | valid | invalid
  const [promoInfo, setPromoInfo] = useState(null);
  const promoDebounce = useRef(null);
  const [googleIdentity, setGoogleIdentity] = useState(null); // { email, name, token }
  const [googleStep, setGoogleStep] = useState(1); // 1 = plan & coupon, 2 = company details

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("google_prefill_data");
      if (raw) {
        const data = JSON.parse(raw);
        setGoogleIdentity(data);
        setGoogleStep(1);
        setForm((f) => ({ ...f, ownerEmail: data.email || "", ownerName: data.name || "" }));
        sessionStorage.removeItem("google_prefill_data");
      }
    } catch (_) { /* ignore malformed prefill payload */ }
  }, []);

  const clearGoogleIdentity = () => {
    setGoogleIdentity(null);
    setGoogleStep(1);
    setForm((f) => ({ ...f, ownerEmail: "", ownerPassword: "" }));
  };

  useEffect(() => {
    const code = couponCode.trim();
    if (couponDebounce.current) clearTimeout(couponDebounce.current);
    if (!code) {
      setCouponState("idle");
      setCouponInfo(null);
      return;
    }
    setCouponState("checking");
    couponDebounce.current = setTimeout(() => {
      affiliateApi.validateCoupon(code)
        .then((res) => {
          if (res.valid) {
            setCouponState("valid");
            setCouponInfo(res);
          } else {
            setCouponState("invalid");
            setCouponInfo(null);
          }
        })
        .catch(() => { setCouponState("invalid"); setCouponInfo(null); });
    }, 450);
    return () => { if (couponDebounce.current) clearTimeout(couponDebounce.current); };
  }, [couponCode]);

  useEffect(() => {
    const code = promoCode.trim();
    if (promoDebounce.current) clearTimeout(promoDebounce.current);
    if (!code) {
      setPromoState("idle");
      setPromoInfo(null);
      return;
    }
    setPromoState("checking");
    promoDebounce.current = setTimeout(() => {
      billingApi.validatePromo(code)
        .then((res) => {
          if (res.valid) {
            setPromoState("valid");
            setPromoInfo(res);
          } else {
            setPromoState("invalid");
            setPromoInfo(null);
          }
        })
        .catch(() => { setPromoState("invalid"); setPromoInfo(null); });
    }, 450);
    return () => { if (promoDebounce.current) clearTimeout(promoDebounce.current); };
  }, [promoCode]);

  useEffect(() => {
    publicPlansApi.list().then((p) => {
      setPlans(p);
      const wanted = params.get("plan");
      const fromParam = wanted && p.find((x) => x.id === wanted);
      const fallback = p.find((x) => x.name === "Pro") || p[1] || p[0];
      const chosen = fromParam || fallback;
      if (chosen) setPlanId(chosen.id);
    }).catch(() => toast.error("Could not load plans."));
  }, [params]);

  const selectedPlan = plans.find((p) => p.id === planId);
  const seatPrice = selectedPlan ? (term === "annual" ? selectedPlan.perSeatAnnual : selectedPlan.perSeatMonthly) : 0;
  const minSeats = selectedPlan?.minSeats || 1;
  const seats = Math.max(minSeats, Number(form.seats) || 1);
  const total = seatPrice * seats;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!planId) { setError("Please select a plan."); return; }
    setSubmitting(true);
    try {
      await billingApi.signupCompany({
        companyName: form.companyName.trim(),
        ownerName: form.ownerName.trim() || null,
        ownerEmail: form.ownerEmail.trim().toLowerCase(),
        ownerPassword: googleIdentity ? undefined : form.ownerPassword,
        googleToken: googleIdentity ? googleIdentity.token : undefined,
        contactPhone: form.contactPhone.trim(),
        planId,
        term,
        seats,
        couponCode: couponCode.trim() || undefined,
        promoCode: promoCode.trim() || undefined,
      });
      toast.success(
        couponState === "valid"
          ? `Welcome aboard! Your ${couponInfo?.trialDays || 30}-day free trial has started.`
          : "Welcome aboard! Your 14-day free trial has started."
      );
      trackEvent("sign_up", { method: "free_trial", plan_id: planId, seats, coupon_applied: couponState === "valid", promo_applied: promoState === "valid" });
      await refreshMe();
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  const seo = getPageSeo("/signup");
  return (
    <div className="min-h-screen flex bg-slate-50">
      <SEO
        title={seo.title}
        description={seo.description}
        path="/signup"
        jsonLd={[softwareApplicationSchema()]}
      />
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[42%] bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-12 relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-[48px] w-[48px] rounded-xl bg-white flex items-center justify-center overflow-hidden shadow-lg">
            <img src="/brand-logo.png" alt="ServiceGuru Pro" className="h-full w-full object-contain" />
          </div>
          <div className="text-white font-bold text-[18px] tracking-tight">ServiceGuru Pro</div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-white text-4xl font-bold leading-tight mb-4">
            Start your 14-day<br />free trial.
          </h1>
          <p className="text-slate-300 text-[14px] leading-relaxed mb-6">
            Everything your field-service team needs — dispatch, CRM, estimates, invoicing, payroll and AI diagnostics — in one workspace.
          </p>
          <ul className="space-y-2.5">
            {["No credit card required to start", "Per-seat pricing — pay only for your team", "Cancel anytime"].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-slate-200 text-[13.5px]">
                <Check className="h-4 w-4 text-emerald-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-slate-500 text-[11px]">© 2026 ServiceGuru Pro</div>
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px",
        }} />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-10 overflow-y-auto bg-[#DBDBDB]">
        <div className="w-full max-w-md">
          {!googleIdentity && (
            <>
              <h2 className="text-[26px] font-bold text-slate-900 mb-1.5">Create your workspace</h2>
              <p className="text-[13.5px] text-slate-500 mb-6">Set up your company and admin account.</p>
            </>
          )}
          {googleIdentity && googleStep === 1 && (
            <>
              <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="signup-google-step-label">Step 1 of 2</div>
              <h2 className="text-[26px] font-bold text-slate-900 mb-1.5">Choose your plan</h2>
              <p className="text-[13.5px] text-slate-500 mb-6">Pick a plan and apply a discount code if you have one.</p>
            </>
          )}
          {googleIdentity && googleStep === 2 && (
            <>
              <div className="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-blue-600" data-testid="signup-google-step-label">Step 2 of 2</div>
              <h2 className="text-[26px] font-bold text-slate-900 mb-1.5">Company details</h2>
              <p className="text-[13.5px] text-slate-500 mb-6">Just a few more details to finish setting up your workspace.</p>
            </>
          )}

          {!googleIdentity ? (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
              <GoogleButton label="Continue with Google" signupPrefill testid="signup-google-button" />
              <OrDivider />
              <Field icon={Building2} label="Company name" value={form.companyName} onChange={set("companyName")} placeholder="Acme HVAC" required testid="signup-company-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field icon={User} label="Your name" value={form.ownerName} onChange={set("ownerName")} placeholder="Jane Doe" testid="signup-owner-name" />
                <Field icon={Phone} label="Phone" value={form.contactPhone} onChange={set("contactPhone")} placeholder="(555) 123-4567" testid="signup-phone" />
              </div>
              <Field icon={Mail} type="email" label="Work email" value={form.ownerEmail} onChange={set("ownerEmail")} placeholder="you@company.com" required testid="signup-email" />
              <Field icon={Lock} type="password" label="Password" value={form.ownerPassword} onChange={set("ownerPassword")} placeholder="At least 6 characters" required testid="signup-password" />

              <PlanAndCouponSection
                couponCode={couponCode} setCouponCode={setCouponCode} couponState={couponState} couponInfo={couponInfo}
                promoCode={promoCode} setPromoCode={setPromoCode} promoState={promoState} promoInfo={promoInfo}
                plans={plans} term={term} setTerm={setTerm} planId={planId} setPlanId={setPlanId}
                seatsValue={form.seats} onSeatsChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))}
                minSeats={minSeats} total={total}
              />

              {error && (
                <div data-testid="signup-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[14.5px] text-red-700">{error}</div>
              )}

              <button
                type="submit" disabled={submitting}
                data-testid="signup-submit"
                className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[16px] font-semibold disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Start free trial
              </button>
            </form>
          ) : googleStep === 1 ? (
            <div className="space-y-4" data-testid="signup-google-step1">
              <div data-testid="signup-google-identity-badge" className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="text-[13px] text-blue-800 truncate">
                    Signing up as <span className="font-semibold">{googleIdentity.email}</span> via Google
                  </div>
                </div>
                <button type="button" onClick={clearGoogleIdentity} data-testid="signup-google-identity-clear" className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold shrink-0">
                  Use different email
                </button>
              </div>

              <PlanAndCouponSection
                couponCode={couponCode} setCouponCode={setCouponCode} couponState={couponState} couponInfo={couponInfo}
                promoCode={promoCode} setPromoCode={setPromoCode} promoState={promoState} promoInfo={promoInfo}
                plans={plans} term={term} setTerm={setTerm} planId={planId} setPlanId={setPlanId}
                seatsValue={form.seats} onSeatsChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))}
                minSeats={minSeats} total={total}
              />

              <button
                type="button"
                disabled={!planId}
                data-testid="signup-google-step1-continue"
                onClick={() => setGoogleStep(2)}
                className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[16px] font-semibold disabled:opacity-60"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-google-step2">
              <div data-testid="signup-google-identity-badge" className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="text-[13px] text-blue-800 truncate">
                    Signing up as <span className="font-semibold">{googleIdentity.email}</span> via Google
                  </div>
                </div>
                <button type="button" onClick={clearGoogleIdentity} data-testid="signup-google-identity-clear" className="text-[12px] text-blue-600 hover:text-blue-800 font-semibold shrink-0">
                  Use different email
                </button>
              </div>

              <Field icon={Building2} label="Company name" value={form.companyName} onChange={set("companyName")} placeholder="Acme HVAC" required testid="signup-company-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field icon={User} label="Your name" value={form.ownerName} testid="signup-owner-name" disabled />
                <Field icon={Phone} label="Phone" value={form.contactPhone} onChange={set("contactPhone")} placeholder="(555) 123-4567" testid="signup-phone" />
              </div>
              <Field icon={Mail} type="email" label="Work email" value={form.ownerEmail} testid="signup-email" disabled />

              {error && (
                <div data-testid="signup-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[14.5px] text-red-700">{error}</div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="signup-google-step2-back"
                  onClick={() => setGoogleStep(1)}
                  className="h-11 px-5 inline-flex items-center justify-center rounded-lg border border-slate-400 text-slate-700 text-[15px] font-semibold hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="submit" disabled={submitting}
                  data-testid="signup-submit"
                  className="flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[16px] font-semibold disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  Start free trial
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center text-[12.5px] text-slate-500">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-login-link" className="text-blue-600 hover:text-blue-700 font-semibold">Sign in</Link>
          </div>

          <div className="mt-4 text-center text-[11.5px] text-slate-400">
            By starting your trial you agree to our{" "}
            <Link to="/terms" data-testid="signup-terms-link" className="hover:text-slate-600">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" data-testid="signup-privacy-link" className="hover:text-slate-600">Privacy Policy</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange = () => {}, placeholder, required, disabled, testid }) {
  return (
    <div>
      <label className="block text-[14px] font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type={type} required={required} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
          data-testid={testid}
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-400 text-[15.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-100 disabled:text-slate-500"
        />
      </div>
    </div>
  );
}
